/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import * as React from 'react';

import {
    getPayloadObject,
    getTelemetryAttributes,
    IPayLoad,
    ITelemetryContent,
    TelemetryConstant
} from '@msdyn365-commerce-modules/utilities';
import MsDyn365 from '@msdyn365-commerce/core';
// VSI Customization - starts
import { OrgUnitContact, OrgUnitLocation, StoreHours } from '@msdyn365-commerce/retail-proxy';
import { getTimeZoneAbbr, secondsToTime } from './date-time-helpers';
// VSI Customization - ends

export interface IStoreSelectorLocationLineItemResources {
    contactInfoHeader: string;
    storeHoursHeader: string;
    availabilityInfoHeader: string;

    closedText: string;

    outOfStock: string;
    inStock: string;
    currentLocation: string;
    selectLocation: string;
    selectLocationAriaLabelFormat: string;
    setAsPreferredStoreText: string;
    setAsPreferredStoreTextAriaLabel: string;
    preferredStoreText: string;
    preferredStoreAriaLabel: string;

    days: {
        monday: string;
        tuesday: string;
        wednesday: string;
        thursday: string;
        friday: string;
        saturday: string;
        sunday: string;
        mondayFull: string;
        tuesdayFull: string;
        wednesdayFull: string;
        thursdayFull: string;
        fridayFull: string;
        saturdayFull: string;
        sundayFull: string;
    };
    // VSI Customization - starts
    viewStoreDetails: string;
    storeOpenAtHours: string;
    storeCloseAtHours: string;
    // VSI Customization - ends
}

export interface IStoreSelectorLocationLineItemProps {
    location: OrgUnitLocation;
    storeHours?: StoreHours;

    hideStockStatus?: boolean;
    isInStock?: boolean;
    isCurrentLocation?: boolean;
    resources: IStoreSelectorLocationLineItemResources;
    stockStatusLabel?: string;
    storeLocatorView?: boolean;
    isPreferredStoreEnabled?: boolean;
    index: string;
    preferredStoreLocationId: string | null;
    /** The telemetry content */
    telemetryContent?: ITelemetryContent;
    preferredDistanceUnit: string;
    isLocationDisabled?: boolean;
    handlers: {
        onSelected(location: OrgUnitLocation): void;
        onSetAsPreferredStore(locationId: string | undefined): void;
        onRemovePreferredStore(): void;
    };
}

const StoreSelectorLocationLineItemComponentActions = {
    onLocationSelected(props: IStoreSelectorLocationLineItemProps): void {
        props.handlers.onSelected(props.location);
    },
    onSetAsPreferredStore(props: IStoreSelectorLocationLineItemProps): void {
        props.handlers.onSetAsPreferredStore(props.location.OrgUnitNumber);
    },
    onRemovePreferredStore(props: IStoreSelectorLocationLineItemProps): void {
        props.handlers.onRemovePreferredStore();
    }
};

const renderStoreHours = (
    location: OrgUnitLocation,
    resources: IStoreSelectorLocationLineItemResources,
    distanceAsString: string | undefined
): JSX.Element | null => {
    const isClosedToday = location.ExtensionProperties?.find(x => x.Key === 'Yext_Today_IsClosed')?.Value?.BooleanValue;
    const isClosed = location.ExtensionProperties?.find(x => x.Key === 'Yext_Is_Store_Closed')?.Value?.BooleanValue;
    const startTime = location.ExtensionProperties?.find(x => x.Key === 'Yext_Today_OpenFrom')?.Value?.IntegerValue;
    const endTime = location.ExtensionProperties?.find(x => x.Key === 'Yext_Today_OpenTo')?.Value?.IntegerValue;
    const timeZone = location.ExtensionProperties?.find(x => x.Key === 'Channel_TimeZone_InfoId')?.Value?.StringValue;
    const timeZoneAbbr = getTimeZoneAbbr(timeZone);
    if (isClosedToday && isClosedToday !== null) {
        return (
            <div className={`ms-store-select__store-hours-details-group ${distanceAsString?.length && 'has-location'}`}>
                <div className='ms-store-select__store-hours-details'>{resources.closedText}</div>
            </div>
        );
    } else if (startTime !== null || endTime !== null) {
        return (
            <div className={`ms-store-select__store-hours-details-group ${distanceAsString?.length && 'has-location'}`}>
                <div className={!isClosed ? 'ms-store-select__store-hours-details open' : 'ms-store-select__store-hours-details'}>
                    {!isClosed
                        ? `${resources.storeCloseAtHours} ${secondsToTime(endTime)} ${timeZoneAbbr}`
                        : `${resources.storeOpenAtHours} ${secondsToTime(startTime)} ${timeZoneAbbr}`}
                </div>
            </div>
        );
    } else {
        return null;
    }
};

const buildDistanceString = (
    distance: number | undefined,
    preferredDistanceUnit: string,
    isLocationDisabled?: boolean
): string | undefined => {
    if (distance === undefined || isLocationDisabled) {
        return undefined;
    } else if (distance < 1) {
        // VSI Customization - starts
        return `<1 ${preferredDistanceUnit}`;
    } else {
        return `${Math.floor(distance)} ${preferredDistanceUnit}`;
        // VSI Customization - ends
    }
};

const extractPhoneNumber = (contacts: OrgUnitContact[] | undefined): string | undefined => {
    if (!contacts) {
        return undefined;
    }

    const allPhoneContacts = contacts.filter(contact => contact.ContactTypeValue === 1 && !contact.IsPrivate);

    if (allPhoneContacts && allPhoneContacts.length > 0) {
        const primaryPhoneContact = allPhoneContacts.find(contact => contact.IsPrimary);

        if (primaryPhoneContact) {
            return primaryPhoneContact.Locator;
        }

        return allPhoneContacts[0].Locator;
    }

    return undefined;
};

// tslint:disable-next-line: cyclomatic-complexity max-func-body-length
const StoreSelectorLocationLineItem: React.FC<IStoreSelectorLocationLineItemProps> = (props: IStoreSelectorLocationLineItemProps) => {
    const onClick = () => StoreSelectorLocationLineItemComponentActions.onLocationSelected(props);
    const _storeDetailsTracking = () => {
        // @ts-ignore
        if ('utag' in window) {
            const pageURL = MsDyn365.isBrowser && window.location.pathname;
            // @ts-ignore
            utag.link({
                event: "store-locator-slideout-store-details",
                event_category: "store locator slideout",
                event_action: "store details link click",
                event_label: `from: ${pageURL}`,
                event_noninteraction: "false"
            });
        }
    };
    const {
        hideStockStatus,
        isCurrentLocation,
        isInStock,
        location,
        resources,
        // storeHours,
        stockStatusLabel,
        storeLocatorView,
        index,
        preferredStoreLocationId,
        isPreferredStoreEnabled,
        isLocationDisabled,
        telemetryContent
    } = props;

    const storeName: string | undefined =
        location.ExtensionProperties?.find(x => x.Key === 'Yext_Store_Name')?.Value?.StringValue || location.OrgUnitName;
    const storeLocatorContext = location && location.Contacts ? location.Contacts.find(url => url.ContactTypeValue === 3) : undefined;
    const storeLocatorUrl: string | undefined = storeLocatorContext
        ? storeLocatorContext.Locator
        : `https://bing.com/maps/default.aspx?where1=${location.Address}`;
    const distanceAsString: string | undefined = buildDistanceString(location.Distance, props.preferredDistanceUnit, isLocationDisabled);
    const storeAddress: string | undefined = location.Address;
    // VSI Customization - starts
    const storeAddressLine1: string | undefined = location.Street;
    const storeAddressLine2: string | undefined = `${location.City}, ${location.State} ${location.Zip}`;
    // VSI Customization - ends
    const phoneNumber: string | undefined = extractPhoneNumber(location.Contacts);

    const resourceLabel = isInStock ? resources.inStock : resources.outOfStock;
    const stockLabel = stockStatusLabel ? stockStatusLabel : undefined;
    const stockText: string | undefined = hideStockStatus ? undefined : stockLabel ? stockLabel : resourceLabel;
    const canSelectLocation = hideStockStatus || isInStock;
    const pickupInStoreButtonText: string = resources.selectLocation;
    const pickupInStoreAriaLabel: string = resources.selectLocationAriaLabelFormat.replace('{StoreName}', storeName || '');
    const isPreferredStore = preferredStoreLocationId === location.OrgUnitNumber;
    const payLoad: IPayLoad = getPayloadObject('click', telemetryContent!, TelemetryConstant.PickupInStore);
    const attributes = getTelemetryAttributes(telemetryContent!, payLoad);
    payLoad.contentAction.etext = TelemetryConstant.PreferredStore;
    const preferredStoreAttr = getTelemetryAttributes(telemetryContent!, payLoad);
    payLoad.contentAction.etext = TelemetryConstant.RemovePreferredStore;
    const RemovePreferredStoreAttr = getTelemetryAttributes(telemetryContent!, payLoad);
    const setAsPreferredStore = () => StoreSelectorLocationLineItemComponentActions.onSetAsPreferredStore(props);
    const removePreferredStore = () => StoreSelectorLocationLineItemComponentActions.onRemovePreferredStore(props);

    return (
        <div className='ms-store-select__location-line-item'>
            <div className='ms-store-select__location-line-item-header'>
                {storeLocatorView && <span className='ms-store-select__location-line-item-store-index'>{index}</span>}
                <span className='ms-store-select__location-line-item-store-name'>{storeName}</span>
            </div>
            {/* VSI Customization - starts */}
            <div className='ms-store-select__location-line-item-store-hours'>
                <p className='ms-store-select__location-line-header'>{resources.storeHoursHeader}</p>
                {renderStoreHours(location, resources, distanceAsString)}
                {distanceAsString ? <span className='ms-store-select__location-line-item-store-distance'> {distanceAsString}</span> : false}
            </div>
            {/* VSI Customization - starts */}
            <div className='ms-store-select__location-line-item-content'>
                <div className='ms-store-select__location-line-item-contact-info'>
                    <p className='ms-store-select__location-line-header'>{resources.contactInfoHeader}</p>
                    {storeAddress && (
                        <div className='ms-store-select__location-line-shop-address-section'>
                            <span className='msi msi-map-marker-alt ms-store-select__location-line-shop-address-glyph-icon' />
                            <div className='ms-store-select__location-line-shop-address-text'>
                                <p>{storeAddressLine1}</p>
                                <p>{storeAddressLine2}</p>
                            </div>
                        </div>
                    )}
                    {phoneNumber && (
                        <div className='ms-store-select__location-line-shop-phone-section'>
                            <span className='msi msi-phone ms-store-select__location-line-shop-phone-icon' />
                            <span className='ms-store-select__location-line-shop-phone-text'>{phoneNumber}</span>
                        </div>
                    )}
                </div>
                {!storeLocatorView && (
                    <div className='ms-store-select__location-line-item-store-availability'>
                        <p className='ms-store-select__location-line-header'>{resources.availabilityInfoHeader}</p>
                        {stockText && <p className='ms-store-select__location-line-stock-status'>{stockText}</p>}
                        {isCurrentLocation ? (
                            <p className='ms-store-select__location-line-current-location'>{resources.currentLocation}</p>
                        ) : canSelectLocation ? (
                            <button
                                className='ms-store-select__location-line-select-store'
                                aria-label={pickupInStoreAriaLabel}
                                onClick={onClick}
                                {...attributes}
                            >
                                {pickupInStoreButtonText}
                            </button>
                        ) : (
                                    false
                                )}
                    </div>
                )}
                <div className='ms-store-select__location-line-item-btn-holder'>
                    {isPreferredStoreEnabled ? (
                        isPreferredStore ? (
                            <button
                                className='ms-store-select__location-line-item-preferred-store'
                                aria-label={resources.preferredStoreAriaLabel}
                                onClick={removePreferredStore}
                                {...RemovePreferredStoreAttr}
                            >
                                {resources.preferredStoreText}
                            </button>
                        ) : (
                                <button
                                    className='ms-store-select__location-line-item-set-as-preferred-store'
                                    aria-label={resources.setAsPreferredStoreTextAriaLabel}
                                    {...preferredStoreAttr}
                                    onClick={setAsPreferredStore}
                                >
                                    {resources.setAsPreferredStoreText}
                                </button>
                            )
                    ) : null}
                    <a className='ms-store-select__location-line-item-store-details' href={storeLocatorUrl} target='_blank' onClick={_storeDetailsTracking}>
                        {resources.viewStoreDetails}
                    </a>
                </div>
            </div>
        </div>
    );
};

export const StoreSelectorLocationLineItemComponent = StoreSelectorLocationLineItem as (
    props: IStoreSelectorLocationLineItemProps
) => JSX.Element;
